import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { trackContactAction } from "@/utils/contactTracking";
import { GlassContainer, GlassCard } from "@/components/experimental/GlassUI";

const SpaBooking: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section
      id="spa-booking"
      data-spa-booking-anchor="true"
      tabIndex={-1}
      className="py-20 px-4"
      data-oid="-:cwcbh"
    >
      <div className="max-w-6xl mx-auto" data-oid="a:vq8dm">
        <div className="text-center mb-16" data-oid="v66ylck">
          <GlassContainer intensity="low" className="inline-block px-8 py-6 backdrop-blur-md">
            <h2
              className="text-4xl md:text-5xl font-playfair text-gray-900 mb-6"
              data-oid="zrbunzz"
            >
              {t("spa.booking.title")}
            </h2>
            <p
              className="text-xl text-gray-800 max-w-3xl mx-auto"
              data-oid="a:5.1b9"
            >
              {t("spa.booking.description")}
            </p>
          </GlassContainer>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-oid="f5m1nn4">
          {/* Contact Information */}
          <GlassContainer
            className="p-8 shadow-xl"
            intensity="medium"
            data-oid="ipkpg9g"
          >
            <h3
              className="text-2xl font-playfair font-semibold text-gray-900 mb-6"
              data-oid="eowwxjp"
            >
              {t("spa.booking.contactInfoTitle")}
            </h3>

            <div className="space-y-6" data-oid="aiv0vch">
              <div className="flex items-start" data-oid="d.ya0mw">
                <MapPin
                  className="w-6 h-6 text-alpine-gold mr-3 mt-1"
                  data-oid="55ugc7a"
                />

                <div data-oid="1cqza.c">
                  <h4
                    className="font-semibold text-gray-800 mb-1"
                    data-oid=".sxqd7o"
                  >
                    Address
                  </h4>
                  <a
                    href="https://www.google.com/maps/place/Parkhotel+Beatenberg/@46.6976742,7.7919849,17z/data=!3m1!4b1!4m9!3m8!1s0x478fa541a569a32d:0x786359f5bdf1a5e4!5m2!4m1!1i2!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F1tkc5ln2?entry=ttu&g_ep=EgoyMDI1MDkwOS4wIKXMDSoASAFQAw%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-900 whitespace-pre-line hover:text-alpine-gold transition-colors duration-200 underline"
                    data-oid="4h00h4u"
                  >
                    {t("spa.booking.address")}
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-oid="e7ak9f9">
                <Phone
                  className="w-6 h-6 text-alpine-gold mr-3 mt-1"
                  data-oid="1jegitl"
                />

                <div data-oid="np_-acz">
                  <h4
                    className="font-semibold text-gray-800 mb-1"
                    data-oid="vgrnqae"
                  >
                    Reservations
                  </h4>
                  <a
                    href="tel:+41338410021"
                    onClick={() =>
                      trackContactAction("spa", "phone", {
                        phone_number: "+41338410021",
                        contact_source: "spa_booking_direct",
                        page_location: "spa_booking_section",
                      })
                    }
                    className="contact-action-track text-alpine-gold hover:text-alpine-gold/80 font-medium transition-colors duration-200 underline"
                    data-oid=".mk.u-8"
                  >
                    +41 33 841 00 21
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-oid="i.3xgk6">
                <Mail
                  className="w-6 h-6 text-alpine-gold mr-3 mt-1"
                  data-oid="2:_wubh"
                />

                <div data-oid="0ikjl32">
                  <h4
                    className="font-semibold text-gray-800 mb-1"
                    data-oid="i.:-83b"
                  >
                    Email
                  </h4>
                  <a
                    href="mailto:info@beatenberg-resortspa.ch"
                    onClick={() =>
                      trackContactAction("spa", "email", {
                        email_address: "info@beatenberg-resortspa.ch",
                        contact_source: "spa_booking_direct",
                        page_location: "spa_booking_section",
                      })
                    }
                    className="contact-action-track text-alpine-gold hover:text-alpine-gold/80 font-medium transition-colors duration-200 underline break-all"
                    data-oid="dox6au8"
                  >
                    info@beatenberg-resortspa.ch
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-oid="._jyv:f">
                <MessageCircle
                  className="w-6 h-6 text-alpine-gold mr-3 mt-1"
                  data-oid="-so9roi"
                />

                <div className="flex-1" data-oid="h1.3:o5">
                  <h4
                    className="font-semibold text-gray-800 mb-2"
                    data-oid="cb65ph3"
                  >
                    WhatsApp
                  </h4>
                  <div className="space-y-2" data-oid="r7gsk53">
                    <a
                      href="https://wa.me/41766220122"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackContactAction("spa", "whatsapp", {
                          region: "europe",
                          whatsapp_number: "+41766220122",
                          contact_source: "spa_booking_direct",
                          page_location: "spa_booking_section",
                        })
                      }
                      className="contact-action-track block text-alpine-gold hover:text-alpine-gold/80 font-medium transition-colors duration-200 underline"
                      data-oid="n-gucr."
                    >
                      Europe: +41 76 622 01 22
                    </a>
                    <a
                      href="https://wa.me/41796968608"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackContactAction("spa", "whatsapp", {
                          region: "asia",
                          whatsapp_number: "+41796968608",
                          contact_source: "spa_booking_direct",
                          page_location: "spa_booking_section",
                        })
                      }
                      className="contact-action-track block text-alpine-gold hover:text-alpine-gold/80 font-medium transition-colors duration-200 underline"
                      data-oid="b97onus"
                    >
                      Asia: +41 79 696 86 08
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-start" data-oid="jfahcin">
                <Clock
                  className="w-6 h-6 text-alpine-gold mr-3 mt-1"
                  data-oid="xa1xyqu"
                />

                <div data-oid="nbqljyz">
                  <h4
                    className="font-semibold text-gray-800 mb-1"
                    data-oid="1yn7ozh"
                  >
                    Accessibility
                  </h4>
                  <p
                    className="text-gray-900 whitespace-pre-line"
                    data-oid="zhjxku_"
                  >
                    {t("spa.booking.accessibility")}
                  </p>
                </div>
              </div>
            </div>
          </GlassContainer>

          {/* Guest Benefits */}
          <GlassContainer
            className="p-8 shadow-xl"
            intensity="medium"
            data-oid="2itzzr4"
          >
            <h3
              className="text-2xl font-playfair font-semibold text-gray-900 mb-6"
              data-oid="c.2alo1"
            >
              {t("spa.booking.guestBenefitsTitle")}
            </h3>

            <div className="space-y-4" data-oid="lrzhv.1">
              <div className="flex items-start" data-oid="zsk0sgd">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="z34c8is">
                  •
                </span>
                <span className="text-gray-900" data-oid="p8.rjew">
                  {t("spa.booking.detoxTea")}
                </span>
              </div>
              <div className="flex items-start" data-oid="hvwo63a">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="s7f3acm">
                  •
                </span>
                <span className="text-gray-900" data-oid="vf.i6ks">
                  {t("spa.booking.winterGarden")}
                </span>
              </div>
              <div className="flex items-start" data-oid="pj.b0u5">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="0rvgnsf">
                  •
                </span>
                <span className="text-gray-900" data-oid="y__-mw:">
                  {t("spa.booking.guestCardBenefits")}
                </span>
              </div>
              <div className="flex items-start" data-oid=".jvofz7">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="fiqtgt:">
                  •
                </span>
                <span className="text-gray-900" data-oid="r7wci:x">
                  {t("spa.booking.advanceReservations")}
                </span>
              </div>
              <div className="flex items-start" data-oid="_pmr3xu">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="rakdl8g">
                  •
                </span>
                <span className="text-gray-900" data-oid="t3mt:ws">
                  {t("spa.booking.packageDeals")}
                </span>
              </div>
              <div className="flex items-start" data-oid="_nxgt.h">
                <span className="text-alpine-gold mr-2 mt-1" data-oid="f0l9qts">
                  •
                </span>
                <span className="text-gray-900" data-oid="g4r.jc0">
                  {t("spa.booking.groupPrograms")}
                </span>
              </div>
            </div>

            <div
              className="mt-8 pt-6 border-t border-gray-300"
              data-oid="qbgo7nd"
            >
              <Button
                asChild
                trackingType="booking"
                trackingLabel="Book Your Stay"
                trackingLocation="spa_booking_section"
                className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                data-oid="y3axv8b"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  className="w-full"
                  data-oid="74w:7hb"
                >
                  Book Your Stay
                </a>
              </Button>
            </div>
          </GlassContainer>
        </div>
      </div>
    </section>
  );
};

export default SpaBooking;
